/**
 * Per-device memory of pop-out windows (browser storage; no PHI): each
 * module's last window size and position, the follow/lock choice, and the
 * "workspace" (which pop-outs were open together last time) for Restore my
 * workspace. Patient ids are never stored: a per-patient pop-out reopens on
 * the patient selected at that moment.
 */
import { readJson, readString, writeJson, writeString } from '../lib/storage.js';
import { parseGeometry, type Geometry } from './popout.js';

const PREFIX = 'velora.win.v1';
const geomKey = (key: string) => `${PREFIX}.geom.${key}`;
const PIN_KEY = `${PREFIX}.pinned`;
const WORKSPACE_KEY = `${PREFIX}.workspace`;
const REOPEN_KEY = `${PREFIX}.reopen`;

export function readGeometry(key: string): Geometry | null {
  return parseGeometry(readJson<unknown>(geomKey(key), null));
}

export function saveGeometry(key: string, g: Geometry): void {
  writeJson(geomKey(key), g);
}

/** The modules whose per-patient pop-outs open locked (the person turned Follow off). */
export function readPinnedDefault(key: string): boolean {
  const list = readJson<unknown>(PIN_KEY, []);
  return Array.isArray(list) && list.includes(key);
}

export function savePinnedDefault(key: string, pinned: boolean): void {
  const list = readJson<unknown>(PIN_KEY, []);
  const keys = Array.isArray(list) ? list.filter((k): k is string => typeof k === 'string') : [];
  const next = pinned ? [...new Set([...keys, key])] : keys.filter((k) => k !== key);
  writeJson(PIN_KEY, next);
}

/** "Reopen my pop-outs when Velora opens" (off unless turned on). */
export function readReopen(): boolean {
  return readString(REOPEN_KEY) === '1';
}

export function saveReopen(on: boolean): void {
  writeString(REOPEN_KEY, on ? '1' : null);
}

export interface WorkspaceEntry {
  key: string;
  /** A page without a patient in it (e.g. /analytics/money), else null: the module's own page. */
  path: string | null;
  pinned: boolean;
  openedAt: number;
  /** Last heartbeat of the window. */
  seenAt: number;
  closedAt: number | null;
}

export type Workspace = Record<string, WorkspaceEntry>;

/** Windows closed within this long of the last one count as one workspace (a browser quit). */
export const BATCH_MS = 15_000;
/** A window with no heartbeat for this long is treated as closed (crash, killed tab). */
export const STALE_MS = 60_000;
const KEEP_MS = 30 * 24 * 3600_000;

function isEntry(v: unknown): v is WorkspaceEntry {
  if (!v || typeof v !== 'object') return false;
  const e = v as Record<string, unknown>;
  return (
    typeof e['key'] === 'string' &&
    (e['path'] === null || typeof e['path'] === 'string') &&
    typeof e['openedAt'] === 'number' &&
    typeof e['seenAt'] === 'number' &&
    (e['closedAt'] === null || typeof e['closedAt'] === 'number')
  );
}

export function readWorkspace(): Workspace {
  const raw = readJson<unknown>(WORKSPACE_KEY, {});
  const out: Workspace = {};
  if (raw && typeof raw === 'object') {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (isEntry(v)) out[k] = { ...v, pinned: v.pinned === true };
    }
  }
  return out;
}

/** Records this module's window as open (or still open), keyed by module. */
export function touchWorkspace(
  ws: Workspace,
  entry: Pick<WorkspaceEntry, 'key' | 'path' | 'pinned'>,
  now: number,
): Workspace {
  const prev = ws[entry.key];
  const openedAt = prev && prev.closedAt === null ? prev.openedAt : now;
  const next: Workspace = {};
  for (const [k, v] of Object.entries(ws)) {
    if (now - (v.closedAt ?? v.seenAt) < KEEP_MS) next[k] = v;
  }
  next[entry.key] = { ...entry, openedAt, seenAt: now, closedAt: null };
  return next;
}

export function closeInWorkspace(ws: Workspace, key: string, now: number): Workspace {
  const prev = ws[key];
  if (!prev) return ws;
  return { ...ws, [key]: { ...prev, seenAt: now, closedAt: now } };
}

/**
 * The pop-outs to reopen: the windows that were closed together most
 * recently (within BATCH_MS of the last close, which is what quitting the
 * browser or closing the main window looks like), plus any still marked open
 * whose heartbeat stopped. A pop-out closed by hand earlier is not reopened.
 */
export function lastWorkspace(ws: Workspace, now: number): WorkspaceEntry[] {
  const ended = (e: WorkspaceEntry) => e.closedAt ?? (now - e.seenAt > STALE_MS ? e.seenAt : null);
  const entries = Object.values(ws);
  const times = entries.map(ended).filter((t): t is number => t !== null);
  if (times.length === 0) return [];
  const last = Math.max(...times);
  return entries
    .filter((e) => {
      const t = ended(e);
      return t !== null && t >= last - BATCH_MS;
    })
    .sort((a, b) => a.openedAt - b.openedAt);
}

export function writeWorkspace(ws: Workspace): void {
  writeJson(WORKSPACE_KEY, ws);
}

/** Read-modify-write of the workspace (each window writes only its own module's entry). */
export function updateWorkspace(fn: (ws: Workspace) => Workspace): void {
  writeWorkspace(fn(readWorkspace()));
}
